<script>
  import { createEventDispatcher, tick, onMount, onDestroy } from "svelte";
  import { createInput, defaults } from "input-core";
  import Icon from "@shared/svelte/Components/Icon.svelte";

  export let value = "";
  export let defaultValue = undefined;
  export let reformat = undefined;
  export let maskString = undefined;
  export let maskChar = defaults.maskChar;
  export let mask = defaults.mask;
  export let maskFormat = defaults.maskFormat;
  export let alwaysShowMask = false;
  export let showMask = false;
  export let action; // this hacked version of svelte-imask allows a use action
  export let id;
  export let name = "";
  export let label = "";
  export let description = "";
  export let errorMessage = "";

  const KEYBOARD_CODE = {
    BACKSPACE: 8,
    DELETE: 46,
  };
  const KEYBOARD_KEY = {
    BACKSPACE: "Backspace",
    DELETE: "Delete",
  };
  const CHARTYPES = {
    USER: 1,
    STATIC: 2,
    MASK: 3,
  };
  const dispatch = createEventDispatcher();
  const input = createInput({
    value: value || defaultValue || "",
    reformat,
    maskString,
    maskChar,
    mask,
    maskFormat,
  });

  let shouldShowMask = alwaysShowMask || showMask;
  $: shouldShowMask = alwaysShowMask || showMask;
  $: input.setReformat(reformat);
  $: input.setMaskFormat(maskFormat);
  $: input.setMask(mask);
  $: input.setMaskString(maskString);
  $: input.setMaskChar(maskChar);
  $: value !== undefined && input.setValue(value);

  onMount(() => {
    input.subscribe(applyValue);
  });

  onDestroy(() => {
    input.unsubscribe(applyValue);
  });

  let canSetSelection = false;
  let inputValue = setupInputValue(input.getState());

  let inputEl;

  function setupInputValue({ maskedValue, visibleValue }) {
    if (visibleValue.replace(/\D/g, "") == "") {
      return "";
    }

    if (shouldShowMask && (canSetSelection || alwaysShowMask)) {
      return maskedValue;
    }
    if (visibleValue.replace(/\D/g, "") == "") {
      return "";
    }

    return visibleValue;
  }

  function applyValue({ maskedValue, visibleValue, selection, value }) {
    inputValue = setupInputValue({ maskedValue, visibleValue });
    setSelection(selection);
    refreshValue();
    dispatchChangeEvent({
      unmasked: reformat ? value : unmaskedValue(value),
      maskedValue,
      visibleValue,
    });
  }

  function unmaskedValue(value) {
    return value
      .filter(item => item.type === CHARTYPES.USER)
      .map(item => item.char)
      .join("");
  }

  // Editing characters can put the input.value in an error-prone state. This refreshes the state by resetting the input.value to either the current value or a given inputValue.
  function refreshValue(inputValue) {
    const unmasked = unmaskedValue(inputValue || input.getState().value);
    input.setValue(unmasked);
  }

  // Inserts userInput into the current input value. This will set the new input value from scratch,
  //   and replaces `input.input(userInput)` and `input.paste(userInput)`, which is error-prone depending on the state of `input.value`.
  function setInput(userInput) {
    const modifiedInputValue = constructInputValueWithUserInput(userInput);

    refreshValue(modifiedInputValue);
    setSelectionAfterUserInput(userInput);
  }

  // Constructs roughly what input value should look like after inserting userInput.
  function constructInputValueWithUserInput(userInput) {
    const currentValue = input.getState().value;

    const valuesBeforeStartSelection = currentValue.slice(0, inputEl.selectionStart);
    const valuesAfterEndSelection = currentValue.slice(inputEl.selectionEnd, currentValue.length);
    const newValues = {
      char: userInput,
      type: CHARTYPES.USER,
    };

    return valuesBeforeStartSelection.concat(newValues, valuesAfterEndSelection);
  }

  // Places selection at the position following the inputValue, accounting for the static chars
  //  (static chars being the parentheses, space, and dash in, for example, this mask "(000) 000-0000").
  // Example: With "|" representing the selection location in phone number "(987|) 543-210",
  //   inserting a "6" at the selection location should result in a selection like so: "(987) 6|54-3210"
  function setSelectionAfterUserInput(userInput) {
    const inputValue = input.getState().value;

    let newSelectionPosition = inputEl.selectionStart + userInput.length; // where selection would be if no static chars exist
    for (
      let i = inputEl.selectionStart;
      i < Math.min(newSelectionPosition, inputValue.length);
      i++
    ) {
      if (inputValue[i].type === CHARTYPES.STATIC) {
        newSelectionPosition++; // essentially moving past the static char
      }
    }
    input.setSelection({ start: newSelectionPosition, end: newSelectionPosition });
  }

  async function setSelection({ start, end }) {
    if (!canSetSelection) {
      return;
    }

    await tick();
    inputEl.setSelectionRange(start, end);
    const raf =
      window.requestAnimationFrame ||
      window.webkitRequestAnimationFrame ||
      window.mozRequestAnimationFrame ||
      (fn => setTimeout(fn, 0));
    // For android
    raf(() => inputEl.setSelectionRange(start, end));
  }

  function setupSelection() {
    input.setSelection({
      start: inputEl.selectionStart,
      end: inputEl.selectionEnd,
    });
  }

  function getValue() {
    if (showMask && (canSetSelection || alwaysShowMask)) {
      return input.getState().maskedValue;
    } else {
      return input.getState().visibleValue;
    }
  }

  // for setting whole field
  function handleInput(e) {
    const prevValue = getValue();

    // fix conflict by update value in mask model
    if (e.target.value !== prevValue) {
      input.setSelection({ start: 0, end: prevValue.length });
      input.input(e.target.value);
      setSelection(input.getSelection());
      // Timeout needed for IE
      setTimeout(() => setSelection(input.getSelection()), 0);
    }
  }

  // for pasting via CTRL+V or right-click menu
  function handlePaste(e) {
    e.preventDefault();
    setupSelection();

    // getData value needed for IE also works in FF & Chrome
    setInput(e.clipboardData.getData("Text"));
    setSelection(input.getSelection());
    // Timeout needed for IE
    setTimeout(() => setSelection(input.getSelection()), 0);
  }

  // for entering a single character
  function handleKeyPress(e) {
    if (e.metaKey || e.altKey || e.ctrlKey || e.key === "Enter") {
      return;
    }
    e.preventDefault();
    setupSelection();
    setInput(e.key || e.data || String.fromCharCode(e.which));
    setSelection(input.getSelection());
  }

  function handleKeyDown(e) {
    if (e.which === KEYBOARD_CODE.BACKSPACE || e.key === KEYBOARD_KEY.BACKSPACE) {
      e.preventDefault();
      setupSelection();
      input.removePreviosOrSelected();
      setSelection(input.getSelection());
    }

    if (e.which === KEYBOARD_CODE.DELETE || e.key === KEYBOARD_KEY.DELETE) {
      e.preventDefault();
      setupSelection();
      input.removeNextOrSelected();
      setSelection(input.getSelection());
    }
  }

  function handleFocus(e) {
    canSetSelection = true;
    dispatch("focus", e);
  }

  function handleBlur(e) {
    canSetSelection = false;
    dispatch("blur", e);
  }

  function dispatchChangeEvent({ unmasked, maskedValue, visibleValue }) {
    dispatch("change", {
      element: inputEl,
      inputState: { unmaskedValue: unmasked, maskedValue, visibleValue },
    });
  }

  let actionFunc;
  let actionParams;

  if (action) {
    actionFunc = action[0];
    actionParams = action[1];
  } else {
    actionFunc = _ => {};
    actionParams = null;
  }

</script>

<style>
  .fizz-input-text:disabled {
    background-color: #eff0f0;
  }

</style>

<div>
  {#if label}
    <label class="fizz-label fizz-stack-4" for={id}>{label}</label>
  {/if}
  <input
    {...$$restProps}
    value={inputValue}
    {id}
    name={name || id}
    on:input={handleInput}
    on:keydown={handleKeyDown}
    on:keypress={handleKeyPress}
    on:paste={handlePaste}
    on:focus={handleFocus}
    on:blur={handleBlur}
    bind:this={inputEl}
    use:actionFunc={actionParams}
  />
  {#if description}
    <small class="fizz-text-subdued">
      {description}
    </small>
  {/if}
  {#if errorMessage}
    <div class="fizz-flex-row-4">
      <Icon type="Error" height="16" width="16" />
      <p class="fizz-text-medium fizz-text-negative">{errorMessage}</p>
    </div>
  {/if}
</div>
